import { Injectable, InputSignal, Signal, computed } from '@angular/core';
import {
    EquipmentType,
    EquipmentTypeSchema,
    ForecastTimeInterval,
    ForecastTimeIntervalSchema,
    HistoricalTimeInterval,
    HistoricalTimeIntervalSchema,
    IIqTableRateStateService,
    LaneInfo,
    Quote,
    RateType,
    RateTypeSchema,
    Row,
    dailyLabel,
    getCurrencyLabel,
    getMileCurrencyLabel,
    monthLabel,
} from '../../models';
import { DatiqGraphqlApolloService } from '@dat/datiq-graphql';
import { IDatiqGeoSuggestion, IqProxyGeoService } from '@dat/iq-proxy';
import { DocumentNode } from 'graphql';
import { gql } from 'apollo-angular';
import { kebabCase } from 'lodash';
import { firstValueFrom } from 'rxjs';
import { DatiqLogService } from '@dat/datiq-log';
import { z } from 'zod';
import { DateTime } from 'luxon';

const LaneRateSchema = z.object({
    date: z.string(),
    average: z.number().nullable(),
    range: z.object({
        low: z.number().nullable(),
        high: z.number().nullable(),
    }),
});

type LaneRate = z.infer<typeof LaneRateSchema>;

const LaneSchema = z.object({
    mileage: z.number(),
    rates: z.object({
        trip: z.array(LaneRateSchema),
        mile: z.array(LaneRateSchema).optional().nullable(),
    }),
});

type Lane = z.infer<typeof LaneSchema>;

const LaneDataSchema = z.object({
    data: z.object({
        lane: LaneSchema,
    }),
});

type LaneData = z.infer<typeof LaneDataSchema>;

@Injectable({
    providedIn: 'root',
})
export class IqTableRateStateService implements IIqTableRateStateService {
    private _queryMap: Record<
        ForecastTimeInterval | HistoricalTimeInterval,
        DocumentNode
    > = {
        THIRTYFIVEDAYS: this.Forcast35DQuery,
        THIRTEENMONTHSUMMARY: this.Forecast13M,
        Monthly: this.Historical13M,
    };

    private _queryVariablesCreator: Record<
        ForecastTimeInterval | HistoricalTimeInterval,
        (
            origin: IDatiqGeoSuggestion,
            destination: IDatiqGeoSuggestion,
            equipment: EquipmentType,
            rateType: RateType,
        ) => any
    > = {
        THIRTYFIVEDAYS: (
            origin: IDatiqGeoSuggestion,
            destination: IDatiqGeoSuggestion,
            equipment: EquipmentType,
            rateType: RateType,
        ) => {
            return {
                query: {
                    equipmentCategory: equipment,
                    rateType: rateType,
                    maximumEscalation: 'COUNTRY',
                    forecastPeriod: 'THIRTYFIVEDAYS',
                    origin: {
                        city: origin.city,
                        stateProv: origin.stateProv,
                        postalCode: origin.postalCode,
                        bestMatch: '',
                    },
                    destination: {
                        city: destination.city,
                        stateProv: destination.stateProv,
                        postalCode: destination.postalCode,
                        bestMatch: '',
                    },
                },
            };
        },
        THIRTEENMONTHSUMMARY: (
            origin: IDatiqGeoSuggestion,
            destination: IDatiqGeoSuggestion,
            equipment: EquipmentType,
            rateType: RateType,
        ) => {
            return {
                query: {
                    equipmentCategory: equipment,
                    rateType: rateType,
                    maximumEscalation: 'COUNTRY',
                    forecastPeriod: 'THIRTEENMONTHSUMMARY',
                    origin: {
                        city: origin.city,
                        stateProv: origin.stateProv,
                        postalCode: origin.postalCode,
                        bestMatch: '',
                    },
                    destination: {
                        city: destination.city,
                        stateProv: destination.stateProv,
                        postalCode: destination.postalCode,
                        bestMatch: '',
                    },
                },
            };
        },
        Monthly: (
            origin: IDatiqGeoSuggestion,
            destination: IDatiqGeoSuggestion,
            equipment: EquipmentType,
            rateType: RateType,
        ) => {
            return {
                query: {
                    equipmentCategory: equipment,
                    rateType: rateType,
                    timeInterval: 'Monthly',
                    timeEscalation: 'SEVENDAYS',
                    origin: {
                        city: origin.city,
                        stateProv: origin.stateProv,
                        postalCode: origin.postalCode,
                    },
                    destination: {
                        city: destination.city,
                        stateProv: destination.stateProv,
                        postalCode: destination.postalCode,
                    },
                },
            };
        },
    };

    get Forcast35DQuery(): DocumentNode {
        return gql`
            query ($query: Ratecast_SearchCriteriaInput) {
                lane: RatecastPublic(query: $query) {
                    mileage
                    rates: forecasts {
                        trip: perTrip {
                            date: forecastDate
                            average: forecastUSD
                            range: mae {
                                low: lowUSD
                                high: highUSD
                            }
                        }
                        mile: perMile {
                            date: forecastDate
                            average: forecastUSD
                            range: mae {
                                low: lowUSD
                                high: highUSD
                            }
                        }
                    }
                }
            }
        `;
    }
    get Forecast13M(): DocumentNode {
        return gql`
            query ($query: Ratecast_SearchCriteriaInput) {
                lane: RatecastPublic(query: $query) {
                    mileage
                    rates: forecasts {
                        trip: perTrip {
                            date: forecastDate
                            average: forecastUSD
                            range: mae {
                                low: lowUSD
                                high: highUSD
                            }
                        }
                        mile: perMile {
                            date: forecastDate
                            average: forecastUSD
                            range: mae {
                                low: lowUSD
                                high: highUSD
                            }
                        }
                    }
                }
            }
        `;
    }
    get Historical13M(): DocumentNode {
        return gql`
            query ($query: HISTORICAL_SearchCriteriaInputHistorical) {
                lane: getHistorical(query: $query) {
                    mileage
                    rates {
                        trip {
                            date
                            average
                            range {
                                low
                                high
                            }
                        }
                        mile {
                            date
                            average
                            range {
                                low
                                high
                            }
                        }
                    }
                }
            }
        `;
    }

    private _cache: Record<
        ForecastTimeInterval | HistoricalTimeInterval,
        Record<string, Row[]>
    > = {
        THIRTYFIVEDAYS: {},
        THIRTEENMONTHSUMMARY: {},
        Monthly: {},
    };
    private _laneInfo: Record<
        ForecastTimeInterval | HistoricalTimeInterval,
        Record<string, LaneInfo>
    > = {
        THIRTYFIVEDAYS: {},
        THIRTEENMONTHSUMMARY: {},
        Monthly: {},
    };

    constructor(
        private readonly _graphQl: DatiqGraphqlApolloService,
        private readonly _geo: IqProxyGeoService,
        private readonly _log: DatiqLogService,
    ) {}

    rowsHistorical13M(
        origin: InputSignal<string>,
        destination: InputSignal<string>,
        rateType: InputSignal<RateType>,
        equipment: InputSignal<EquipmentType>,
        quote: InputSignal<Quote>,
        useMiles: InputSignal<boolean>,
    ): Signal<Promise<Row[]>> {
        
        return computed(async () => {
            return await this._callInfo(
                origin(),
                destination(),
                rateType(),
                equipment(),
                quote(),
                useMiles(),
                'Monthly',
            );
        });
    }

    rowsForecast13M(
        origin: InputSignal<string>,
        destination: InputSignal<string>,
        rateType: InputSignal<RateType>,
        equipment: InputSignal<EquipmentType>,
        quote: InputSignal<Quote>,
        useMiles: InputSignal<boolean>,
    ): Signal<Promise<Row[]>> {
        return computed(async () => {
            return await this._callInfo(
                origin(),
                destination(),
                rateType(),
                equipment(),
                quote(),
                useMiles(),
                'THIRTEENMONTHSUMMARY',
            );
        });
    }
    rowsForecast35D(
        origin: InputSignal<string>,
        destination: InputSignal<string>,
        rateType: InputSignal<RateType>,
        equipment: InputSignal<EquipmentType>,
        quote: InputSignal<Quote>,
        useMiles: InputSignal<boolean>,
    ): Signal<Promise<Row[]>> {
        return computed(async () => {
            return await this._callInfo(
                origin(),
                destination(),
                rateType(),
                equipment(),
                quote(),
                useMiles(),
                'THIRTYFIVEDAYS',
            );
        });
    }

    private async _callInfo(
        origin: string,
        destination: string,
        rateType: RateType,
        equipment: EquipmentType,
        quote: Quote,
        useMiles: boolean,
        timeInterval: ForecastTimeInterval | HistoricalTimeInterval,
    ): Promise<Row[]> {
        const validQuery: boolean = this._validateQueries(
            origin,
            destination,
            rateType,
            equipment,
            timeInterval,
        );

        if (!validQuery) {
            return [];
        }

        const id = this._createId(
            origin,
            destination,
            rateType,
            equipment,
            useMiles,
        );

        if (!!this._cache?.[timeInterval]?.[id]?.length) {
            return this._updateRate(id, quote, timeInterval, useMiles);
        }

        try {
            const originSuggestion = await this._geo.fetchFirstResult(origin);
            const destinationSuggestion =
                await this._geo.fetchFirstResult(destination);
            const variables = this._queryVariablesCreator[timeInterval](
                originSuggestion,
                destinationSuggestion,
                equipment,
                rateType,
            );
            const query = this._queryMap[timeInterval];
            const response: LaneData = await firstValueFrom(
                this._graphQl.getApollo().query({
                    query,
                    variables,
                }),
            );

            LaneDataSchema.parse(response); // Checks to see if the response is valid.

            this._laneInfo[timeInterval][id] = {
                mileage: response.data.lane.mileage,
            };

            this._cache[timeInterval][id] = this._mapRows(
                response,
                useMiles,
                timeInterval === 'THIRTYFIVEDAYS',
            );

            return this._updateRate(id, quote, timeInterval, useMiles);
        } catch (e) {
            console.error(e);
            this._log.error(e);
            return [];
        }
    }
    private _mapRows(
        response: LaneData,
        useMiles: boolean,
        daily: boolean,
    ): Partial<Row[]> {
        const lane: Lane = response.data.lane;
        const rates: LaneRate[] = useMiles ? lane.rates.mile : lane.rates.trip;

        return rates.map((rate: LaneRate, index: number): Row => {
            const date = DateTime.fromISO(rate.date, { zone: 'utc' });
            const high = rate.range.high;
            const average = rate.average;
            const low = rate.range.low;

            return {
                date: {
                    id: `date-${index}`,
                    label: daily ? dailyLabel(date) : monthLabel(date),
                    data: date.toJSDate(),
                },
                high: {
                    id: `high-${index}`,
                    label: getCurrencyLabel(high, useMiles),
                    data: high,
                },
                average: {
                    id: `average-${index}`,
                    label: `**${getCurrencyLabel(average, useMiles)}**`,
                    data: average,
                },
                low: {
                    id: `low-${index}`,
                    label: getCurrencyLabel(low, useMiles),
                    data: low,
                },
            };
        });
    }

    private _updateRate(
        id: string,
        quote: Quote,
        timeInterval,
        useMiles: boolean,
    ): Row[] {
        const rows: Row[] = this._cache?.[timeInterval]?.[id];
        const laneInfo: LaneInfo = this._laneInfo?.[timeInterval]?.[id];

        return rows.map((row) => {
            const fuel = quote?.fuel ?? 0;
            const accessorial = useMiles ? fuel : fuel * laneInfo.mileage;
            const high: number = (row?.high?.data as number) + accessorial;
            const average = (row?.average?.data as number) + accessorial;
            const low = (row?.low?.data as number) + accessorial;

            return {
                date: row.date,
                high: {
                    ...row.high,

                    label: getCurrencyLabel(high, useMiles),
                    data: high,
                },
                average: {
                    ...row.average,
                    label: `**${getCurrencyLabel(average, useMiles)}**`,
                    data: average,
                },
                low: {
                    ...row.low,
                    label: getCurrencyLabel(low, useMiles),
                    data: low,
                },
            };
        });
    }
    private _createId(
        origin: string,
        destination: string,
        rateType: string,
        equipment: string,
        useMiles: boolean,
    ) {
        return [origin, destination, rateType, equipment, useMiles]
            .map(kebabCase)
            .join('~');
    }
    private _validateQueries(
        origin: string,
        destination: string,
        rateType: RateType,
        equipment: EquipmentType,
        timeInterval: ForecastTimeInterval | HistoricalTimeInterval,
    ): boolean {
        const validOdPair: boolean = !!origin?.length && !!destination?.length;
        const validRateType: boolean =
            RateTypeSchema.safeParse(rateType).success;
        const validEquipmentType: boolean =
            EquipmentTypeSchema.safeParse(equipment).success;
        const validTimeInterval: boolean =
            ForecastTimeIntervalSchema.safeParse(timeInterval).success ||
            HistoricalTimeIntervalSchema.safeParse(timeInterval).success;

        return (
            validOdPair &&
            validRateType &&
            validEquipmentType &&
            validTimeInterval
        );
    }
}
